/**
 * CanvasAnimator - Animated canvas renderer with timeline controls
 *
 * Wraps CanvasRenderer with animation state management.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { CanvasRenderer } from './CanvasRenderer'
import { THUMBNAIL_VIEW_BOX } from '@/constants'

export interface CanvasAnimatorProps {
	blockId: string
	className?: string
	autoPlay?: boolean
	duration?: number // seconds
	onComplete?: () => void
}

const DEFAULT_DURATION = 3

export function CanvasAnimator({
	blockId,
	className,
	autoPlay = false,
	duration = DEFAULT_DURATION,
	onComplete,
}: CanvasAnimatorProps) {
	const [animationTime, setAnimationTime] = useState(0)
	const [isPlaying, setIsPlaying] = useState(autoPlay)

	// Refs to avoid restarting the animation loop on every frame
	const animationTimeRef = useRef(animationTime)
	animationTimeRef.current = animationTime
	const onCompleteRef = useRef(onComplete)
	onCompleteRef.current = onComplete

	// Animation loop — deps exclude animationTime and onComplete (via refs)
	useEffect(() => {
		if (!isPlaying) return

		const startTime = Date.now() - animationTimeRef.current * duration * 1000
		let frameId: number

		const animate = () => {
			const elapsed = (Date.now() - startTime) / 1000
			const progress = Math.min(elapsed / duration, 1)

			setAnimationTime(progress)

			if (progress < 1) {
				frameId = requestAnimationFrame(animate)
			} else {
				setIsPlaying(false)
				onCompleteRef.current?.()
			}
		}

		frameId = requestAnimationFrame(animate)
		return () => cancelAnimationFrame(frameId)
	}, [isPlaying, duration])

	const handlePlayPause = useCallback(() => {
		if (animationTime >= 1) {
			setAnimationTime(0)
		}
		setIsPlaying((p) => !p)
	}, [animationTime])

	const handleReset = useCallback(() => {
		setAnimationTime(0)
		setIsPlaying(false)
	}, [])

	return (
		<div className={className}>
			<CanvasRenderer
				blockId={blockId}
				mode="animated"
				viewBox={THUMBNAIL_VIEW_BOX}
				showField={true}
				animationTime={animationTime}
			/>

			{/* Animation controls */}
			<div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
				<button onClick={handlePlayPause}>{isPlaying ? 'Pause' : 'Play'}</button>
				<button onClick={handleReset}>Reset</button>
				<input
					type="range"
					min={0}
					max={1}
					step={0.01}
					value={animationTime}
					onChange={(e) => {
						setAnimationTime(parseFloat(e.target.value))
						setIsPlaying(false)
					}}
					style={{ flex: 1 }}
				/>
			</div>
		</div>
	)
}
